import { useMemo, useState } from "react";
import {
  TmaOnboardingReward,
  QUEST_TYPE,
  QUEST_REWARD_TYPE,
  QuestCompletion,
  TMA_QUEST_TYPES,
  METRICS_EVENTS,
  QuestDto,
} from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useQuestClaimRewards } from "./useQuestClaimRewards";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useApi } from "@/hooks/core/useApi";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { enqueueSnackbar } from "notistack";

const questTypes = TMA_QUEST_TYPES.join(",");

interface UseTmaOnboardingRewardsOptions {
  enabled?: boolean;
}

/**
 * Hook for TMA onboarding quests data
 */
export const useTmaOnboardingRewards = (options?: UseTmaOnboardingRewardsOptions) => {
  const queryEnabled = options?.enabled ?? true;
  const { isTMA } = useTelegramContext();
  const { hubUser, auth, userExchanges, authenticated } = useHubUser();
  const apiHelper = useApi();
  const { claimQuestRewards } = useQuestClaimRewards();
  const [isClaimingRewards, setIsClaimingRewards] = useState(false);
  const { trackEvent } = useMetricsEvents();

  // Get all live TMA onboarding quests, no auth required
  // ONLY FETCH ON TMA PLATFORM
  const { data: tmaOnboardingQuests = [], isLoading: isLoadingTmaOnboardingQuests } = useQuery({
    queryKey: [QueryKeys.QUEST_TMA_ONBOARDING_LIVE],
    queryFn: async () => {
      const res = await apiHelper.get(API_ENDPOINT.QUEST_TMA_ONBOARDING_LIVE);
      return Array.isArray(res) ? res : [];
    },
    enabled: queryEnabled && isTMA, // Only fetch when on TMA platform and enabled
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Compute rewards from the quests
  const tmaOnboardingRewards: TmaOnboardingReward = useMemo(() => {
    return computeRewards(tmaOnboardingQuests);
  }, [tmaOnboardingQuests]);

  const userExchangesCount = Object.keys(userExchanges ?? {}).length;

  // Get all user quest completions for TMA onboarding quests
  // ONLY FETCH ON TMA PLATFORM
  const {
    data: userTmaQuestCompletions = [],
    isLoading: isLoadingUserTmaQuestCompletions,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.QUEST_TMA_USER_COMPLETIONS, hubUser?.id, userExchangesCount],
    queryFn: () =>
      apiHelper.getWithAuth(auth, API_ENDPOINT.QUEST_USER_COMPLETIONS, {
        queryParams: { types: questTypes },
      }),
    enabled: queryEnabled && isTMA && !!hubUser?.id, // Only fetch when on TMA platform and user is authenticated
    staleTime: 30_000,
  });

  // Note: No need for manual refetch - React Query auto-refetches when queryKey changes (hubUser?.id, userExchangesCount)

  // Get the newly completed quests to trigger an animation
  const newlyCompletedQuests: QuestCompletion[] = useMemo(() => {
    return userTmaQuestCompletions.filter((completion) => {
      return (
        completion.rewardClaimed === false &&
        new Date(completion.completedAt || new Date()) > new Date(Date.now() - 2 * 60 * 1000)
      );
    });
  }, [userTmaQuestCompletions]);

  // Return true if we must close the onboarding page
  const mustSkipOnboarding: boolean = useMemo(() => {
    if (isLoadingUserTmaQuestCompletions) return false;
    // if there is no quest configured, we don't skip the onboarding page
    if (tmaOnboardingQuests.length === 0) return false;

    // If the user claimed all the lottery tickets rewards
    if (
      tmaOnboardingQuests.every((quest) => {
        return (
          quest.rewardType !== QUEST_REWARD_TYPE.LOTTERY_TICKET ||
          userTmaQuestCompletions.some((completion) => {
            return completion.questId === quest.questId && completion.rewardClaimed === true;
          })
        );
      })
    ) {
      return true;
    }
    // If the user is connected but didn't complete TMA_TON_CONNECTION quest
    // we admit it's because he created his TON account before quests implementation
    if (hubUser?.id && authenticated && userTmaQuestCompletions.length === 0) {
      return true;
    }
    return false;
  }, [tmaOnboardingQuests, isLoadingUserTmaQuestCompletions]);

  // Get the total number of usdt rewards claimable
  const totalUsdtRewardsClaimable: number = useMemo(() => {
    return calculateUsdtRewards(userTmaQuestCompletions, false);
  }, [userTmaQuestCompletions]);

  // Get the total number of usdt rewards received
  const totalUsdtRewardsReceived: number = useMemo(() => {
    return calculateUsdtRewards(userTmaQuestCompletions, true);
  }, [userTmaQuestCompletions]);

  // Fetch the user completions quests until all the TMA onboarding quests are completed and then claim the rewards
  const claimTmaOnboardingRewards = async (): Promise<boolean> => {
    if (!tmaOnboardingQuests?.length || !hubUser?.id) return false;

    setIsClaimingRewards(true);

    try {
      const maxAttempts = 10;
      const delayMs = 500;

      let attempts = 0;
      const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

      while (attempts < maxAttempts) {
        // Refetch user quest completions
        const { data: currentCompletions } = await refetch();

        if (!currentCompletions) {
          attempts++;
          await wait(delayMs);
          continue;
        }

        // Check if all TMA onboarding quests are completed
        const allQuestsCompleted = tmaOnboardingQuests.every((quest) =>
          currentCompletions.some(
            (completion) => completion.questId === quest.questId && completion.rewardClaimed === false,
          ),
        );

        if (allQuestsCompleted) {
          for (const completion of currentCompletions) {
            // Claim only lottery ticket rewards, usdt rewards are claimed when discord report is generated
            if (completion.rewardType === QUEST_REWARD_TYPE.LOTTERY_TICKET && completion.rewardClaimed === false) {
              claimQuestRewards({ questCompletion: completion, showSnackbar: false });
            }
          }
          // Track successful TMA onboarding rewards claim
          trackEvent(METRICS_EVENTS.CLAIM_TMA_ONBOARDING_REWARDS_SUCCESS, {
            hubUserId: hubUser?.id,
          });
          enqueueSnackbar("Rewards claimed successfully", { variant: "success" });
          return true;
        }

        attempts++;
        if (attempts < maxAttempts) {
          await wait(delayMs);
        }
      }
      // Track failed TMA onboarding rewards claim (timeout or error)
      trackEvent(METRICS_EVENTS.CLAIM_TMA_ONBOARDING_REWARDS_ERROR, {
        hubUserId: hubUser?.id,
      });
      enqueueSnackbar("Failed to claim rewards", { variant: "error" });
      return false;
    } finally {
      setIsClaimingRewards(false);
    }
  };

  const result = {
    tmaOnboardingQuests,
    tmaOnboardingRewards,
    isLoadingTmaOnboardingQuests,
    isLoadingUserTmaQuestCompletions,
    userTmaQuestCompletions,
    newlyCompletedQuests,
    mustSkipOnboarding,
    totalUsdtRewardsClaimable,
    totalUsdtRewardsReceived,
    claimTmaOnboardingRewards,
    isClaimingRewards,
  };

  return result;
};

const computeRewards = (quests?: QuestDto[]): TmaOnboardingReward => {
  const init = (): TmaOnboardingReward => ({
    [QUEST_TYPE.TMA_TON_CONNECTION]: {
      [QUEST_REWARD_TYPE.LOTTERY_TICKET]: 0,
      [QUEST_REWARD_TYPE.USDT]: 0,
    },
    [QUEST_TYPE.TMA_FIRST_CEX_CONNECTION]: {
      [QUEST_REWARD_TYPE.LOTTERY_TICKET]: 0,
      [QUEST_REWARD_TYPE.USDT]: 0,
    },
  });

  return (quests ?? []).reduce((acc, q) => {
    const isTmaType =
      q.typeConfig.type === QUEST_TYPE.TMA_TON_CONNECTION || q.typeConfig.type === QUEST_TYPE.TMA_FIRST_CEX_CONNECTION;
    const isSupportedReward =
      q.rewardType === QUEST_REWARD_TYPE.LOTTERY_TICKET || q.rewardType === QUEST_REWARD_TYPE.USDT;
    if (isTmaType && isSupportedReward) acc[q.typeConfig.type][q.rewardType] += q.rewardCount ?? 0;
    return acc;
  }, init());
};

// Helper function to calculate USDT rewards based on claim status
const calculateUsdtRewards = (questCompletions: QuestCompletion[] | undefined, isClaimed: boolean): number => {
  const completions = questCompletions || [];
  return completions
    .reduce((acc, completion) => {
      return acc.add(
        completion?.rewardType === QUEST_REWARD_TYPE.USDT && completion?.rewardClaimed === isClaimed
          ? completion?.rewardCount
          : new Decimal(0),
      );
    }, new Decimal(0))
    .toNumber();
};
